import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { getAuthHeader } from "../lib/auth";

const DEFAULT_TOURNAMENT_ID = "get-active-3v3-75-ovr-2026";

function apiUrl(path) {
  const base = String(api.base || "/api").replace(/\/$/, "");
  const suffix = String(path || "").startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

function assetUrl(path) {
  const value = String(path || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const apiBase = String(api.base || "/api");
  if (/^https?:\/\//i.test(apiBase)) {
    return `${apiBase.replace(/\/api\/?$/i, "")}${value.startsWith("/") ? value : `/${value}`}`;
  }
  return value;
}

async function requestJSON(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: getAuthHeader(),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    mode: "cors",
    credentials: "omit",
  });
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!response.ok) throw new Error(data?.message || data?.error || `Request failed (${response.status})`);
  return data;
}

function statusLabel(status) {
  const value = String(status || "").toLowerCase();
  if (["open", "under_review"].includes(value)) return "OPEN REVIEW";
  if (value === "resolved") return "RESOLVED";
  return value.toUpperCase() || "UNKNOWN";
}

export default function TournamentDisputeAdmin() {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tournamentId = query.get("tournament") || DEFAULT_TOURNAMENT_ID;
  const selectedId = query.get("dispute") || "";
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [data, setData] = useState({ disputes: [], openCount: 0, tournament: null });
  const [scoreDrafts, setScoreDrafts] = useState({});
  const [adminNotes, setAdminNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await requestJSON(`/tournaments/${tournamentId}/admin/disputes`);
      setData({
        disputes: Array.isArray(result?.disputes) ? result.disputes : [],
        openCount: Number(result?.openCount || 0),
        tournament: result?.tournament || null,
      });
      setScoreDrafts((current) => {
        const next = { ...current };
        (result?.disputes || []).forEach((dispute) => {
          if (!next[dispute.id]) {
            next[dispute.id] = { scoreA: Number(dispute.scoreA || 0), scoreB: Number(dispute.scoreB || 0) };
          }
        });
        return next;
      });
    } catch (err) {
      setError(err.message || "Could not load tournament disputes.");
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedId || loading) return;
    const id = window.setTimeout(() => {
      document.getElementById(`dispute-${selectedId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    return () => window.clearTimeout(id);
  }, [selectedId, loading]);

  async function resolve(dispute, decision) {
    const draft = scoreDrafts[dispute.id] || { scoreA: dispute.scoreA || 0, scoreB: dispute.scoreB || 0 };
    const note = String(adminNotes[dispute.id] || "").trim();
    const copy = decision === "resume_match"
      ? "Resume this match and clear the dispute lock?"
      : `Apply ${dispute.teamAName} ${draft.scoreA}–${draft.scoreB} ${dispute.teamBName} as the official score?`;
    if (!window.confirm(copy)) return;

    setBusyId(dispute.id);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/${tournamentId}/admin/disputes/${dispute.id}/resolve`, {
        method: "POST",
        body: {
          decision,
          scoreA: Number(draft.scoreA || 0),
          scoreB: Number(draft.scoreB || 0),
          adminNote: note,
        },
      });
      setMessage(decision === "resume_match" ? "Match resumed and captains were notified." : "Official score applied and captains were notified.");
      await load();
    } catch (err) {
      setError(err.message || "Could not resolve this dispute.");
    } finally {
      setBusyId("");
    }
  }

  const openDisputes = data.disputes.filter((dispute) => ["open", "under_review"].includes(String(dispute.status || "").toLowerCase()));
  const resolvedDisputes = data.disputes.filter((dispute) => !["open", "under_review"].includes(String(dispute.status || "").toLowerCase()));

  return (
    <div className="page" style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.eyebrow}>DISPUTE ADMIN</div>
        <h1 style={styles.title}>Dispute Review Center</h1>
        <p style={styles.subtitle}>Review score conflicts, event-rule violations, roster issues, no-shows, and uploaded screenshot proof for the selected tournament. This page does not grant team approval, payout, tournament setup, communications, or bot-testing access.</p>
        <div style={styles.heroActions}>
          <Link to="/admin" style={styles.backLink}>← Founder Command</Link>
          <span style={styles.openPill}>{data.openCount} open review{data.openCount === 1 ? "" : "s"}</span>
        </div>
      </section>

      {message ? <div style={styles.success}>{message}</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}

      {loading ? (
        <div style={styles.empty}>Loading dispute reviews…</div>
      ) : (
        <>
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <div>
                <div style={styles.eyebrow}>ACTION REQUIRED</div>
                <h2 style={styles.sectionTitle}>Open reviews</h2>
              </div>
              <button type="button" style={styles.refreshButton} onClick={load}>Refresh</button>
            </div>

            {openDisputes.length ? (
              <div style={styles.list}>
                {openDisputes.map((dispute) => (
                  <DisputeCard
                    key={dispute.id}
                    dispute={dispute}
                    highlighted={selectedId === dispute.id}
                    busy={busyId === dispute.id}
                    scoreDraft={scoreDrafts[dispute.id] || { scoreA: dispute.scoreA || 0, scoreB: dispute.scoreB || 0 }}
                    adminNote={adminNotes[dispute.id] || ""}
                    onScoreChange={(side, value) => setScoreDrafts((current) => ({
                      ...current,
                      [dispute.id]: {
                        ...(current[dispute.id] || { scoreA: dispute.scoreA || 0, scoreB: dispute.scoreB || 0 }),
                        [side]: Math.max(0, Math.min(2, Number(value) || 0)),
                      },
                    }))}
                    onNoteChange={(value) => setAdminNotes((current) => ({ ...current, [dispute.id]: value }))}
                    onResume={() => resolve(dispute, "resume_match")}
                    onSetScore={() => resolve(dispute, "set_score")}
                  />
                ))}
              </div>
            ) : (
              <div style={styles.empty}>No open tournament disputes right now.</div>
            )}
          </section>

          {resolvedDisputes.length ? (
            <section style={styles.section}>
              <div style={styles.eyebrow}>REVIEW HISTORY</div>
              <h2 style={styles.sectionTitle}>Resolved disputes</h2>
              <div style={styles.list}>
                {resolvedDisputes.map((dispute) => (
                  <DisputeCard key={dispute.id} dispute={dispute} readOnly />
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function DisputeCard({ dispute, highlighted, busy, scoreDraft, adminNote, onScoreChange, onNoteChange, onResume, onSetScore, readOnly = false }) {
  const proofs = Array.isArray(dispute.proofUrls) ? dispute.proofUrls : [];
  return (
    <article id={`dispute-${dispute.id}`} style={{ ...styles.card, ...(highlighted ? styles.cardHighlighted : {}) }}>
      <div style={styles.cardTop}>
        <div>
          <div style={styles.reason}>{dispute.reasonLabel || "Tournament dispute"}</div>
          <h3 style={styles.matchup}>{dispute.teamAName} vs {dispute.teamBName}</h3>
        </div>
        <span style={readOnly ? styles.resolvedPill : styles.reviewPill}>{statusLabel(dispute.status)}</span>
      </div>

      <div style={styles.metaGrid}>
        <Meta label="Reported by" value={`${dispute.filedByUsername || "System"}${dispute.filedByTeamName ? ` • ${dispute.filedByTeamName}` : ""}`} />
        <Meta label="Against" value={dispute.againstTeamName || "Score conflict between captains"} />
        <Meta label="Score when reported" value={`${dispute.teamAName} ${dispute.scoreA}–${dispute.scoreB} ${dispute.teamBName}`} />
        <Meta label="Filed" value={dispute.createdAt ? new Date(dispute.createdAt).toLocaleString() : "—"} />
      </div>

      <div style={styles.explanationBox}>
        <div style={styles.metaLabel}>BRIEF EXPLANATION</div>
        <div style={styles.explanation}>{dispute.explanation || "No explanation provided."}</div>
      </div>

      {proofs.length ? (
        <div style={styles.proofSection}>
          <div style={styles.metaLabel}>SCREENSHOT PROOF</div>
          <div style={styles.proofGrid}>
            {proofs.map((proof, index) => {
              const src = assetUrl(proof);
              return (
                <a key={`${proof}-${index}`} href={src} target="_blank" rel="noreferrer" style={styles.proofLink}>
                  <img src={src} alt={`Dispute proof ${index + 1}`} style={styles.proofImage} />
                  <span>Open full screenshot ↗</span>
                </a>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={styles.noProof}>No screenshot proof was uploaded.</div>
      )}

      {readOnly ? (
        <div style={styles.resolutionBox}>
          <strong>Resolution:</strong> {dispute.resolution === "set_score" ? "Official score applied" : "Match resumed"}
          {dispute.adminNote ? ` • ${dispute.adminNote}` : ""}
        </div>
      ) : (
        <div style={styles.adminControls}>
          <div style={styles.scoreEditor}>
            <div style={styles.metaLabel}>OFFICIAL SCORE OVERRIDE</div>
            <div style={styles.scoreLine}>
              <span>{dispute.teamAName}</span>
              <input type="number" min="0" max="2" value={scoreDraft.scoreA} disabled={busy} onChange={(event) => onScoreChange("scoreA", event.target.value)} style={styles.scoreInput} />
            </div>
            <div style={styles.scoreLine}>
              <span>{dispute.teamBName}</span>
              <input type="number" min="0" max="2" value={scoreDraft.scoreB} disabled={busy} onChange={(event) => onScoreChange("scoreB", event.target.value)} style={styles.scoreInput} />
            </div>
          </div>

          <label style={styles.noteLabel}>
            Admin note (optional)
            <textarea value={adminNote} disabled={busy} onChange={(event) => onNoteChange(event.target.value)} rows={3} maxLength={1200} style={styles.noteInput} placeholder="What did you decide and why?" />
          </label>

          <div style={styles.actionRow}>
            <button type="button" style={styles.resumeButton} disabled={busy} onClick={onResume}>Resume Match</button>
            <button type="button" style={styles.scoreButton} disabled={busy} onClick={onSetScore}>{busy ? "Saving…" : "Apply Official Score"}</button>
          </div>
        </div>
      )}
    </article>
  );
}

function Meta({ label, value }) {
  return (
    <div style={styles.metaCard}>
      <div style={styles.metaLabel}>{label}</div>
      <div style={styles.metaValue}>{value}</div>
    </div>
  );
}

const styles = {
  page: { maxWidth: 1320, margin: "0 auto", padding: "18px 16px 80px" },
  hero: { border: "1px solid rgba(210, 40, 52, 0.35)", borderRadius: 24, padding: "28px clamp(18px, 4vw, 44px)", background: "linear-gradient(135deg, rgba(80,8,15,.58), rgba(10,10,12,.92))", boxShadow: "0 20px 60px rgba(0,0,0,.35)" },
  eyebrow: { color: "#ff9aa2", fontWeight: 900, letterSpacing: ".16em", fontSize: 12 },
  title: { margin: "8px 0 8px", color: "#fff", fontSize: "clamp(2.2rem, 7vw, 5rem)", lineHeight: .98 },
  subtitle: { margin: 0, color: "rgba(255,255,255,.75)", fontSize: "clamp(1rem, 2.2vw, 1.2rem)", lineHeight: 1.6, maxWidth: 900 },
  heroActions: { marginTop: 20, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  backLink: { textDecoration: "none", color: "#fff", border: "1px solid rgba(255,255,255,.18)", borderRadius: 999, padding: "10px 14px", fontWeight: 800, background: "rgba(255,255,255,.06)" },
  openPill: { borderRadius: 999, padding: "10px 14px", background: "rgba(136,76,4,.45)", color: "#ffd27e", fontWeight: 900, border: "1px solid rgba(255,177,57,.4)" },
  section: { marginTop: 22, border: "1px solid rgba(255,255,255,.11)", borderRadius: 22, background: "rgba(8,8,10,.86)", padding: "22px clamp(14px, 3vw, 28px)" },
  sectionHeader: { display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", flexWrap: "wrap" },
  sectionTitle: { margin: "6px 0 16px", color: "#fff", fontSize: "clamp(1.8rem, 4vw, 3rem)" },
  refreshButton: { border: "1px solid rgba(255,255,255,.17)", borderRadius: 999, background: "rgba(255,255,255,.06)", color: "#fff", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  list: { display: "grid", gap: 16 },
  card: { border: "1px solid rgba(221,53,65,.34)", borderRadius: 18, padding: 18, background: "linear-gradient(145deg, rgba(38,8,12,.9), rgba(7,7,9,.96))" },
  cardHighlighted: { boxShadow: "0 0 0 2px rgba(255,174,67,.9), 0 18px 55px rgba(0,0,0,.45)" },
  cardTop: { display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" },
  reason: { color: "#ff9aa2", fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase", fontSize: 12 },
  matchup: { color: "#fff", margin: "5px 0 0", fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)" },
  reviewPill: { borderRadius: 999, padding: "8px 11px", color: "#ffd27e", background: "rgba(125,69,2,.42)", border: "1px solid rgba(255,181,65,.4)", fontWeight: 900, fontSize: 12 },
  resolvedPill: { borderRadius: 999, padding: "8px 11px", color: "#98f0b5", background: "rgba(14,96,47,.34)", border: "1px solid rgba(67,217,121,.32)", fontWeight: 900, fontSize: 12 },
  metaGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10, marginTop: 16 },
  metaCard: { borderRadius: 12, border: "1px solid rgba(255,255,255,.09)", background: "rgba(255,255,255,.035)", padding: 12 },
  metaLabel: { color: "rgba(255,255,255,.48)", fontWeight: 900, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase" },
  metaValue: { color: "#fff", marginTop: 5, lineHeight: 1.45 },
  explanationBox: { marginTop: 14, borderRadius: 14, background: "rgba(255,255,255,.04)", padding: 14 },
  explanation: { color: "rgba(255,255,255,.86)", marginTop: 7, lineHeight: 1.6, whiteSpace: "pre-wrap" },
  proofSection: { marginTop: 16 },
  proofGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginTop: 9 },
  proofLink: { textDecoration: "none", color: "#ff9aa2", fontWeight: 800, display: "grid", gap: 7 },
  proofImage: { width: "100%", aspectRatio: "16 / 10", objectFit: "cover", borderRadius: 12, border: "1px solid rgba(255,255,255,.12)", background: "#111" },
  noProof: { marginTop: 14, color: "rgba(255,255,255,.48)", fontStyle: "italic" },
  adminControls: { marginTop: 18, borderTop: "1px solid rgba(255,255,255,.08)", paddingTop: 16, display: "grid", gap: 14 },
  scoreEditor: { display: "grid", gap: 8 },
  scoreLine: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", color: "#fff", fontWeight: 800, borderRadius: 12, background: "rgba(255,255,255,.035)", padding: "10px 12px" },
  scoreInput: { width: 64, borderRadius: 10, border: "1px solid rgba(255,255,255,.18)", background: "#080808", color: "#fff", padding: "9px 10px", fontWeight: 900 },
  noteLabel: { color: "rgba(255,255,255,.72)", fontWeight: 800, display: "grid", gap: 7 },
  noteInput: { borderRadius: 12, border: "1px solid rgba(255,255,255,.15)", background: "rgba(0,0,0,.5)", color: "#fff", padding: 12, resize: "vertical", font: "inherit" },
  actionRow: { display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end" },
  resumeButton: { borderRadius: 999, border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.07)", color: "#fff", padding: "11px 15px", fontWeight: 900, cursor: "pointer" },
  scoreButton: { borderRadius: 999, border: "1px solid rgba(255,82,82,.7)", background: "linear-gradient(135deg,#da1724,#7e040c)", color: "#fff", padding: "11px 16px", fontWeight: 900, cursor: "pointer" },
  resolutionBox: { marginTop: 16, borderRadius: 12, background: "rgba(23,111,55,.18)", border: "1px solid rgba(71,210,119,.25)", color: "#b9f7ca", padding: 12, lineHeight: 1.5 },
  empty: { marginTop: 20, borderRadius: 18, padding: 24, textAlign: "center", color: "rgba(255,255,255,.62)", border: "1px dashed rgba(255,255,255,.15)", background: "rgba(0,0,0,.34)" },
  success: { marginTop: 16, borderRadius: 14, padding: 13, color: "#a8f3bf", background: "rgba(17,106,49,.25)", border: "1px solid rgba(61,203,106,.28)" },
  error: { marginTop: 16, borderRadius: 14, padding: 13, color: "#ffb7b7", background: "rgba(128,13,22,.26)", border: "1px solid rgba(255,76,88,.3)" },
};
